import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProvinceTreeCheckbox from "./components/ProvinceTreeCheckbox";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  IconButton,
  Divider,
  Grid,
  TextField,
  Chip,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

/* ICONS */
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import CakeIcon from "@mui/icons-material/Cake";
import KitchenIcon from "@mui/icons-material/Kitchen";
import HomeIcon from "@mui/icons-material/Home";
import PublicIcon from "@mui/icons-material/Public";
import ImageIcon from "@mui/icons-material/Image";
import Autocomplete from "@mui/material/Autocomplete";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import { Switch, FormControlLabel } from "@mui/material";
import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogContent,
  Snackbar,
  Alert,
} from "@mui/material";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";

/* DND */
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/* ================= PAGE ================= */

function AddRecipe() {
  const { t } = useTranslation(["Recettes"]);
  const navigate = useNavigate();
  const userState = useSelector((state) => state.user);
  const { categoryId } = useParams();
  const [categories, setCategories] = useState([]);
  const isFrench = userState.lang === "FR";
  const [uploadProgress, setUploadProgress] = useState(0);
  const [locationTree, setLocationTree] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [form, setForm] = useState({
    nomFr: "",
    nomEn: "",
    recetteFr: "",
    recetteEn: "",
    ingredientFr: "",
    ingredientEn: "",
    decorationFr: "",
    decorationEn: "",
    ustensileFr: "",
    ustensileEn: "",
    portionMinFr: "",
    portionMinEn: "",
    emporterFr: "",
    emporterEn: "",
    dateActivationDebut: "",
    dateActivationFin: "",
    estActif: true,
    hasVisibilityPeriod: false,
    location: "",
    image: null,
    gallery: [],
    videoFr: null,
    videoEn: null,
    idRecetteCategorie: categoryId || "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/CategoryRecipe/GetRecipeCategories`,
          {
            headers: {
              Authorization: `Bearer ${userState.token}`,
            },
          },
        );

        const data = await res.json();
        setCategories(data || []);
      } catch (err) {
        console.error(err);
        setCategories([]);
      }
    };

    if (userState.token) loadCategories();
  }, [userState.token]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Localization/GetProvinceRecipeTree`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          },
        );

        const data = await response.json();
        setLocationTree(data);
      } catch (err) {
        console.error(err);
      }
    };

    if (userState.token) load();
  }, [userState.token]);
  /* ================= HANDLERS ================= */

  const handleSwitch = (e) => {
    const { name, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGallery = (e) => {
    const files = Array.from(e.target.files);
    setForm((prev) => ({
      ...prev,
      gallery: [...prev.gallery, ...files],
    }));
  };

  const removeGalleryItem = (index) => {
    setForm((prev) => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index),
    }));
  };

  const handleMainImage = (e) => {
    setForm((prev) => ({ ...prev, image: e.target.files[0] }));
  };

  const handleVideo = (e) => {
    const { name, files } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: files[0],
    }));
  };

  /* ================= VALIDATION ================= */
  const isRequired = (name) =>
    [
      "nomFr",
      "nomEn",
      "recetteFr",
      "recetteEn",
      "ingredientFr",
      "ingredientEn",
      "decorationFr",
      "decorationEn",
      "ustensileFr",
      "ustensileEn",
    ].includes(name);

  const validate = () => {
    const err = {};

    const requiredFields = [
      "nomFr",
      "nomEn",

      "recetteFr",
      "recetteEn",

      "ingredientFr",
      "ingredientEn",

      "decorationFr",
      "decorationEn",

      "ustensileFr",
      "ustensileEn",
    ];

    requiredFields.forEach((field) => {
      if (!form[field] || form[field].trim() === "") {
        err[field] = "Champ obligatoire";
      }
    });

    /* IMAGE PRINCIPALE 
  if (!form.image) {
    err.image = "Image principale obligatoire";
  }*/

    /* CATEGORY */
    if (!form.idRecetteCategorie) {
      err.idRecetteCategorie = "Catégorie obligatoire";
    }

    /* LOCATION */
    if (!selectedLocations || selectedLocations.length === 0) {
      err.locations = "Sélectionne une localisation";
    }

    setErrors(err);

    return Object.keys(err).length === 0;
  };
  /* ================= SUBMIT ================= */
  // 👇 AJOUT ICI (AVANT handleUpdate)
  const buildPayload = () => {
    return {
      categoryId,

      /* TEXT */
      NomFr: form.nomFr,
      NomEn: form.nomEn,

      RecetteFr: form.recetteFr,
      RecetteEn: form.recetteEn,

      IngredientFr: form.ingredientFr,
      IngredientEn: form.ingredientEn,

      DecorationFr: form.decorationFr,
      DecorationEn: form.decorationEn,

      UstensileFr: form.ustensileFr,
      UstensileEn: form.ustensileEn,

      PortionMinFr: form.portionMinFr,
      PortionMinEn: form.portionMinEn,

      EmporterFr: form.emporterFr,
      EmporterEn: form.emporterEn,

      /* STATUS */
      EstActif: form.estActif,

      DateActivationDebut: form.dateActivationDebut || new Date().toISOString(),

      DateActivationFin: form.dateActivationFin || new Date().toISOString(),

      /* CATEGORY */
      IdRecetteCategorie: form.idRecetteCategorie,

      /* FILES */
      Image: form.image,

      Gallery: form.gallery || [],

      VideoFr: form.videoFr,

      VideoEn: form.videoEn,

      /* LOCATIONS */
      Locations: selectedLocations || [],
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true); // 👈 IMPORTANT
      setSuccess(false);
      const formData = new FormData();

      /* TEXT */
      formData.append("NomFr", form.nomFr);
      formData.append("NomEn", form.nomEn);
      formData.append("RecetteFr", form.recetteFr);
      formData.append("RecetteEn", form.recetteEn);
      formData.append("IngredientFr", form.ingredientFr);
      formData.append("IngredientEn", form.ingredientEn);
      formData.append("DecorationFr", form.decorationFr);
      formData.append("DecorationEn", form.decorationEn);
      formData.append("UstensileFr", form.ustensileFr);
      formData.append("UstensileEn", form.ustensileEn);
      formData.append("PortionMinFr", form.portionMinFr);
      formData.append("PortionMinEn", form.portionMinEn);
      formData.append("EmporterFr", form.emporterFr);
      formData.append("EmporterEn", form.emporterEn);

      formData.append("EstActif", form.estActif);
      formData.append(
        "DateActivationDebut",
        form.dateActivationDebut || new Date().toISOString(),
      );
      formData.append(
        "DateActivationFin",
        form.dateActivationFin || new Date().toISOString(),
      );

      //formData.append("IdRecetteCategorie", categoryId);
      formData.append("IdRecetteCategorie", form.idRecetteCategorie);

      /* IMAGE */
      if (form.image) {
        formData.append("Image", form.image);
      }

      /* GALLERY */
      (form.gallery || []).forEach((file) => {
        formData.append("Gallery", file);
      });

      /* VIDEO */
      if (form.videoFr) {
        formData.append("VideoFr", form.videoFr);
      }

      if (form.videoEn) {
        formData.append("VideoEn", form.videoEn);
      }

      /* LOCATIONS */
      //formData.append("Locations", JSON.stringify(selectedLocations || []));
      (selectedLocations || []).forEach((loc) => {
        formData.append("Locations", loc);
      });
      /* DEBUG 🔥 */
      const payload = buildPayload();

      console.log(
        "JSON DEBUG 👉",
        JSON.stringify(
          payload,
          (key, value) => {
            if (value instanceof File) {
              return {
                fileName: value.name,
                fileSize: value.size,
                fileType: value.type,
              };
            }

            return value;
          },
          2,
        ),
      );
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/ManageVideoRecipe/CreateRecipe`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
          body: formData,
          credentials: "include",
        },
      );
      const response = await res.json();

      if (!res.ok || !response.success) {
        throw new Error(isFrench ? response.messageFr : response.messageEn);
      }

      /* ===== SUCCESS ===== */
      setLoading(false);
      setSuccess(true);

      setSnackbar({
        open: true,
        message: isFrench ? response.messageFr : response.messageEn,
        severity: "success",
      });

      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } catch (err) {
      setLoading(false);
      setLoading(false);
      setSnackbar({
        open: true,
        message: err.message,
        severity: "error",
      });
    }
  };
  /* ================= DND ================= */

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      setForm((prev) => ({
        ...prev,
        gallery: arrayMove(prev.gallery, active.id, over.id),
      }));
    }
  };

  /* ================= UI ================= */

  return (
    <Box sx={pageStyle}>
      <Paper sx={containerStyle}>
        {/* HEADER */}
        <Box sx={headerStyle}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <LibraryAddIcon color="primary" />

            <Typography fontSize={20} fontWeight={700}>
              {userState.lang === "FR"
                ? "Ajouter une recette"
                : "Add new recipe"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Chip
          label={
            categories.find(
              (c) => String(c.id) === String(form.idRecetteCategorie),
            )
              ? isFrench
                ? categories.find(
                    (c) => String(c.id) === String(form.idRecetteCategorie),
                  ).textFr
                : categories.find(
                    (c) => String(c.id) === String(form.idRecetteCategorie),
                  ).textEn
              : "Aucune catégorie"
          }
          color="primary"
        />
        <Box mt={2}>
          <Autocomplete
            options={categories}
            value={
              categories.find(
                (c) => String(c.id) === String(form.idRecetteCategorie),
              ) || null
            }
            onChange={(event, newValue) => {
              setForm((prev) => ({
                ...prev,
                idRecetteCategorie: newValue ? newValue.id : "",
              }));
            }}
            getOptionLabel={(option) =>
              isFrench ? option.textFr : option.textEn
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <strong>{isFrench ? option.textFr : option.textEn}</strong>
                  <div style={{ fontSize: 12, opacity: 0.6 }}>
                    ID: {option.id}
                  </div>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={isFrench ? "Catégorie de recette" : "Recipe category"}
                placeholder={
                  isFrench ? "Chercher une catégorie..." : "Search category..."
                }
              />
            )}
          />
        </Box>
        {/* section Status */}
        <Section title="Status" icon={<PublicIcon />}>
          {/* ACTIVE */}
          <FormControlLabel
            control={
              <Switch
                checked={form.estActif}
                onChange={handleSwitch}
                name="estActif"
              />
            }
            label={form.estActif ? "Recette active" : "Recette désactivée"}
          />

          {/* VISIBILITY */}
          <FormControlLabel
            control={
              <Switch
                checked={form.hasVisibilityPeriod}
                onChange={handleSwitch}
                name="hasVisibilityPeriod"
              />
            }
            label="Activer période de visibilité"
          />

          {/* DATES */}
          {form.hasVisibilityPeriod && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Date début"
                  name="dateActivationDebut"
                  value={form.dateActivationDebut}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Date fin"
                  name="dateActivationFin"
                  value={form.dateActivationFin}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          )}
        </Section>

        {/* NOM */}
        <Grid container spacing={2} mt={1}>
          <LangField
            label="Nom Français"
            name="nomFr"
            form={form}
            onChange={handleChange}
            errors={errors}
            isRequired={isRequired}
          />
          <LangField
            label="Name English"
            name="nomEn"
            form={form}
            onChange={handleChange}
            lang="EN"
            errors={errors}
            isRequired={isRequired}
          />
        </Grid>

        {/* RECETTE */}
        <Section title="Recette" icon={<RestaurantMenuIcon />}>
          <LangText name="recetteFr" form={form} onChange={handleChange} />
          <LangText
            name="recetteEn"
            form={form}
            onChange={handleChange}
            lang="EN"
            errors={errors}
            isRequired={isRequired}
          />
        </Section>

        {/* INGREDIENTS */}
        <Section title="Ingredients" icon={<ShoppingBasketIcon />}>
          <LangText name="ingredientFr" form={form} onChange={handleChange} />
          <LangText
            name="ingredientEn"
            form={form}
            onChange={handleChange}
            lang="EN"
            errors={errors}
            isRequired={isRequired}
          />
        </Section>

        {/* DECORATION */}
        <Section title="Decoration" icon={<CakeIcon />}>
          <LangText name="decorationFr" form={form} onChange={handleChange} />
          <LangText
            name="decorationEn"
            form={form}
            onChange={handleChange}
            lang="EN"
            errors={errors}
            isRequired={isRequired}
          />
        </Section>

        {/* USTENSILS */}
        <Section title="Utensils" icon={<KitchenIcon />}>
          <LangText name="ustensileFr" form={form} onChange={handleChange} />
          <LangText
            name="ustensileEn"
            form={form}
            onChange={handleChange}
            lang="EN"
            errors={errors}
            isRequired={isRequired}
          />
        </Section>

        {/* PORTION */}
        <Section title="Portion" icon={<HomeIcon />}>
          <LangField name="portionMinFr" form={form} onChange={handleChange} />
          <LangField
            name="portionMinEn"
            form={form}
            onChange={handleChange}
            lang="EN"
            errors={errors}
            isRequired={isRequired}
          />
        </Section>

        {/* PORTION */}
        <Section title="Takeaway" icon={<HomeIcon />}>
          <LangField name="emporterFr" form={form} onChange={handleChange} />
          <LangField
            name="emporterEn"
            form={form}
            onChange={handleChange}
            lang="EN"
            errors={errors}
            isRequired={isRequired}
          />
        </Section>

        {/* MEDIA */}
        <Section title="Media" icon={<ImageIcon />}>
          {/* MAIN IMAGE */}
          <Box sx={uploadBox}>
            <Typography fontWeight={600}>🖼 Image principale *</Typography>
            <Typography variant="caption" color="text.secondary">
              Image affichée comme couverture
            </Typography>

            <Button variant="outlined" component="label" sx={{ mt: 1 }}>
              Choisir une image
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handleMainImage}
              />
            </Button>

            {form.image && (
              <Box mt={1}>
                <Typography fontSize={12}>✔ {form.image.name}</Typography>

                {/* PREVIEW */}
                <Box mt={1}>
                  <img
                    src={URL.createObjectURL(form.image)}
                    style={{
                      width: "100%",
                      maxHeight: 200,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* GALLERY */}
          <Box sx={uploadBox}>
            <Typography fontWeight={600}>
              🖼 Images supplémentaires ({form.gallery.length})
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Ajoute plusieurs images • Glisse pour réorganiser
            </Typography>

            <Button variant="outlined" component="label" sx={{ mt: 1 }}>
              Ajouter des images
              <input
                hidden
                type="file"
                multiple
                accept="image/*"
                onChange={handleGallery}
              />
            </Button>

            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={form.gallery.map((_, i) => i)}
                strategy={horizontalListSortingStrategy}
              >
                <Box sx={galleryBox}>
                  {form.gallery.map((file, index) => (
                    <SortableItem
                      key={index}
                      id={index}
                      file={file}
                      onRemove={removeGalleryItem}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          </Box>

          {/* VIDEO FR */}
          <Box sx={uploadBox}>
            <Typography fontWeight={600}>🎥 Vidéo Française</Typography>

            <Button variant="outlined" component="label" sx={{ mt: 1 }}>
              Ajouter vidéo FR
              <input
                hidden
                type="file"
                accept="video/*"
                name="videoFr"
                onChange={handleVideo}
              />
            </Button>

            {form.videoFr && (
              <Box mt={1}>
                <Typography fontSize={12}>✔ {form.videoFr.name}</Typography>

                <video controls width="100%" style={{ marginTop: 8 }}>
                  <source src={URL.createObjectURL(form.videoFr)} />
                </video>
              </Box>
            )}
          </Box>

          {/* VIDEO EN */}
          <Box sx={uploadBox}>
            <Typography fontWeight={600}>🎥 English Video</Typography>

            <Button variant="outlined" component="label" sx={{ mt: 1 }}>
              Add EN video
              <input
                hidden
                type="file"
                accept="video/*"
                name="videoEn"
                onChange={handleVideo}
              />
            </Button>

            {form.videoEn && (
              <Box mt={1}>
                <Typography fontSize={12}>✔ {form.videoEn.name}</Typography>

                <video controls width="100%" style={{ marginTop: 8 }}>
                  <source src={URL.createObjectURL(form.videoEn)} />
                </video>
              </Box>
            )}
          </Box>
        </Section>

        {/* LOCATION */}
        <Section title="Location" icon={<PublicIcon />}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Sélection des pays et provinces
          </Typography>

          <ProvinceTreeCheckbox
            data={locationTree}
            value={selectedLocations}
            onChange={setSelectedLocations}
          />
        </Section>

        {/* PROGRESS */}
        {uploadProgress > 0 && (
          <Box mt={2}>
            <Typography>{uploadProgress}%</Typography>
            <Box sx={barBg}>
              <Box sx={{ ...barFill, width: `${uploadProgress}%` }} />
            </Box>
          </Box>
        )}

        {/* ACTION */}
        <Stack direction="row" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            Save
          </Button>
        </Stack>
      </Paper>
      <Backdrop
        open={loading}
        sx={{ zIndex: 9999, color: "#fff", backgroundColor: "rgba(0,0,0,0.6)" }}
      >
        <Box textAlign="center">
          <CircularProgress color="inherit" />

          <Typography mt={2}>
            {isFrench ? "Enregistrement en cours..." : "Saving recipe..."}
          </Typography>
        </Box>
      </Backdrop>
      <Dialog open={success}>
        <DialogContent sx={{ textAlign: "center", p: 4 }}>
          <Typography variant="h5">
            🎉 {isFrench ? "Succès" : "Success"}
          </Typography>

          <Typography mt={2}>{snackbar.message}</Typography>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ================= LANG COMPONENTS ================= */
const uploadBox = {
  p: 2,
  mt: 2,
  border: "1px dashed #ccc",
  borderRadius: 2,
  background: "#fafafa",
};
const langBadgeStyle = (lang) => ({
  position: "absolute",
  top: -10,
  left: 10,
  fontSize: 11,
  fontWeight: 700,
  padding: "3px 8px",
  borderRadius: 8,
  background: lang === "FR" ? "#1976d2" : "#2e7d32",
  color: "#fff",
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
});

const LangField = ({
  label,
  name,
  form,
  onChange,
  lang = "FR",
  errors,
  isRequired,
}) => {
  const hasError = !!errors?.[name];

  return (
    <Grid item xs={12} md={6}>
      <Box sx={{ position: "relative", mt: 2 }}>
        <Box sx={langBadgeStyle(lang)}>{lang}</Box>

        <TextField
          fullWidth
          label={label}
          name={name}
          value={form[name]}
          onChange={onChange}
          error={hasError}
          helperText={hasError ? "Champ obligatoire" : ""}
        />
      </Box>
    </Grid>
  );
};
const LangText = ({
  name,
  form,
  onChange,
  lang = "FR",
  errors,
  isRequired,
}) => {
  const hasError = !!errors?.[name];

  return (
    <Box sx={{ position: "relative", mt: 2 }}>
      <Box sx={langBadgeStyle(lang)}>{lang}</Box>

      <TextField
        fullWidth
        multiline
        minRows={3}
        name={name}
        value={form[name]}
        onChange={onChange}
        error={hasError}
        helperText={hasError ? "Champ obligatoire" : ""}
      />
    </Box>
  );
};

/* ================= SORTABLE ================= */

function SortableItem({ id, file, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        ...thumb,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <img src={URL.createObjectURL(file)} style={img} />
      <Button onClick={() => onRemove(id)} sx={deleteBtn}>
        ✕
      </Button>
    </Box>
  );
}

/* ================= SECTION ================= */

function Section({ title, icon, children }) {
  return (
    <Paper sx={sectionStyle}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Typography fontWeight={700}>{title}</Typography>
      </Box>
      <Divider sx={{ my: 1 }} />
      {children}
    </Paper>
  );
}

/* ================= STYLES ================= */

const pageStyle = { minHeight: "100vh", background: "#f4f6f8", p: 2 };

const containerStyle = { maxWidth: 1000, mx: "auto", p: 3 };

const sectionStyle = { p: 2, mt: 2, background: "#fff", borderRadius: 3 };

const galleryBox = { display: "flex", gap: 1, mt: 2, flexWrap: "wrap" };

const thumb = { width: 80, height: 80, position: "relative", cursor: "grab" };

const img = { width: "100%", height: "100%", objectFit: "cover" };

const deleteBtn = { position: "absolute", top: 0, right: 0, minWidth: 0 };

const barBg = { height: 6, background: "#eee", borderRadius: 5 };

const barFill = { height: "100%", background: "#1976d2" };

const headerStyle = { display: "flex", alignItems: "center", gap: 1 };

export default AddRecipe;
